import { useState, useCallback, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Modal,
  Platform,
} from 'react-native';
import { useNavigation, useRoute, RouteProp, useFocusEffect } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Svg, { Path } from 'react-native-svg';
import { COLORS, FONTS } from '../../constants/config';
import { PageContainer, Navbar, Breadcrumb } from '../../components';
import { DropdownPortal, useDropdownAnchor } from '../../components/DropdownPortal';
import { GoalsSkeleton } from '../../components/Skeleton';
import { useRoleNavigation } from '../../hooks';
import { employeesApi, organizationalUnitsApi, kpisApi } from '../../services/apiService';
import { downloadBlobAsFile } from '../../utils/downloadFile';
import { ExportIcon } from '../../components/Icons';
import { useEmployeesQuery } from '../../hooks/queries';
import { queryKeys } from '../../providers/QueryProvider';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { getTextAlign } from '../../utils/rtl';
import { useYear } from '../../contexts/YearContext';
import { Employee } from '../../types';

import { tokens } from '@design';
import { IconButton } from '@design/components/IconButton';
import { Button } from '@design/components/Button';
import { PageHeader } from '@design/components/PageHeader';
type NavigationProp = NativeStackNavigationProp<any>;
type RouteProps = RouteProp<{ Employees: { organizationId?: number; organizationName?: string } }, 'Employees'>;

// Icons
const PlusIcon = ({ size = 16, color = tokens.colors.raw.white }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 5V19M5 12H19" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ChevronDownIcon = ({ size = 14, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M6 9L12 15L18 9" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ChevronLeftIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M15 18L9 12L15 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ChevronRightIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M9 18L15 12L9 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const XIcon = ({ size = 14, color = tokens.colors.raw.red["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M18 6L6 18M6 6L18 18" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const EyeIcon = ({ size = 18, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M1 12C1 12 5 4 12 4C19 4 23 12 23 12C23 12 19 20 12 20C5 20 1 12 1 12Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const EditIcon = ({ size = 18, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M18.5 2.50001C18.8978 2.10219 19.4374 1.87869 20 1.87869C20.5626 1.87869 21.1022 2.10219 21.5 2.50001C21.8978 2.89784 22.1213 3.4374 22.1213 4.00001C22.1213 4.56262 21.8978 5.10219 21.5 5.50001L12 15L8 16L9 12L18.5 2.50001Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const TrashIcon = ({ size = 18, color = tokens.colors.raw.red["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M3 6H5H21" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const FileInputIcon = ({ size = 16, color = tokens.colors.semantic.general.foreground }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V7L15 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V8" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M14 2V8H20" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M2 15H10" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M8 13L10 15L8 17" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const DownloadIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M7 10L12 15L17 10" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 15V3" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const CheckIcon = ({ size = 16, color = tokens.colors.raw.green["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M20 6L9 17L4 12" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

export default function EmployeesScreen() {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<RouteProps>();
  const organizationId = route.params?.organizationId;
  const organizationName = route.params?.organizationName;
  const { isViewOnly } = useRoleNavigation();
  const { colors, isDark } = useTheme();
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';
  const rtlRow = isRTL ? 'row-reverse' as const : 'row' as const;
  const { selectedYear } = useYear();

  // Theme colors
  const themeColors = {
    card: colors.card,
    cardBorder: colors.cardBorder,
    textPrimary: colors.textPrimary,
    textSecondary: colors.textMuted,
    sectionBg: isDark ? colors.backgroundAlt : tokens.colors.raw.slate["50"],
    border: colors.border,
    tableHeader: isDark ? colors.backgroundAlt : tokens.colors.raw.slate["50"],
    tableRow: colors.card,
    tableRowAlt: isDark ? colors.backgroundAlt : '#FAFAFA',
  };

  // Filters
  const [filterDepartment, setFilterDepartment] = useState<string>('all');
  const [filterType, setFilterType] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [showDepartmentDropdown, setShowDepartmentDropdown] = useState(false);
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const statusAnchor = useDropdownAnchor();
  const typeAnchor = useDropdownAnchor();
  const deptAnchor = useDropdownAnchor();

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  // Delete modal state
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [employeeToDelete, setEmployeeToDelete] = useState<Employee | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Feedback message state
  const [feedbackMessage, setFeedbackMessage] = useState<{ type: 'success' | 'info' | 'error'; text: string } | null>(null);

  const queryClient = useQueryClient();

  // Employees main list — React Query renders cached data instantly on focus.
  // Filter changes flow through the query key and trigger a background refetch.
  const employeeQueryParams = useMemo(() => {
    const params: any = {
      page: currentPage,
      pageSize,
      department_id: organizationId,
      year: selectedYear,
    };
    if (filterDepartment !== 'all') params.organizational_unit = Number(filterDepartment);
    if (filterType !== 'all') params.employee_type = filterType;
    if (filterStatus !== 'all') params.is_active = filterStatus === 'active';
    return params;
  }, [currentPage, filterDepartment, filterType, filterStatus, organizationId, selectedYear]);

  const {
    data: employeesResponse,
    isLoading: loading,
    refetch,
  } = useEmployeesQuery(employeeQueryParams);

  const employees: Employee[] = useMemo(
    () => employeesResponse?.results || [],
    [employeesResponse],
  );
  const totalCount = employeesResponse?.count || 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  // Organizational units (for the filter dropdown). Separate query, rarely changes.
  const { data: unitsDropdownData } = useQuery({
    queryKey: ['organizationalUnits', 'dropdown', organizationId],
    queryFn: async () => {
      const params: Record<string, any> = { page_size: 100 };
      if (organizationId) params.department_id = organizationId;
      return organizationalUnitsApi.list(params);
    },
    staleTime: 10 * 60 * 1000,
  });
  const departments = useMemo(
    () =>
      (unitsDropdownData?.results || []).map((unit: any) => ({
        id: unit.id,
        name: unit.name_ar || unit.name,
      })),
    [unitsDropdownData],
  );

  // Refresh on focus — cached data renders instantly, refetch runs silently.
  useFocusEffect(
    useCallback(() => {
      refetch();
    }, [refetch])
  );

  // Reset to page 1 when year changes; query key change triggers a refetch.
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedYear]);

  // Back-compat shim for handlers that called fetchData().
  const fetchData = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: queryKeys.employees.all });
  }, [queryClient]);

  const applyFilters = () => {
    setShowDepartmentDropdown(false);
    setShowTypeDropdown(false);
    setShowStatusDropdown(false);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilterDepartment('all');
    setFilterType('all');
    setFilterStatus('all');
    setShowDepartmentDropdown(false);
    setShowTypeDropdown(false);
    setShowStatusDropdown(false);
    setCurrentPage(1);
  };

  const hasActiveFilters = filterDepartment !== 'all' || filterType !== 'all' || filterStatus !== 'all';

  const handleDelete = (employee: Employee) => {
    setEmployeeToDelete(employee);
    setDeleteModalVisible(true);
  };

  const confirmDelete = async () => {
    if (!employeeToDelete) return;
    setDeleting(true);
    try {
      await employeesApi.delete(employeeToDelete.id);
      setFeedbackMessage({
        type: 'success',
        text: t('employees.deleteSuccess'),
      });
      fetchData();
      // Auto-hide feedback after 3 seconds
      setTimeout(() => setFeedbackMessage(null), 3000);
    } catch (err: any) {
      console.error('Failed to delete employee:', err);
      setFeedbackMessage({
        type: 'error',
        text: t('employees.deleteFailed'),
      });
      setTimeout(() => setFeedbackMessage(null), 3000);
    } finally {
      setDeleting(false);
      setDeleteModalVisible(false);
      setEmployeeToDelete(null);
    }
  };

  const getStatusLabel = (isActive: boolean) => isActive ? t('employees.active') : t('employees.inactive');
  const getStatusColor = (isActive: boolean) => isActive ? tokens.colors.raw.green["500"] : tokens.colors.raw.gray["400"];
  const getTypeLabel = (type?: string) => type === 'manager' ? t('employees.manager') : t('employees.employee');

  const handleExportExcel = async () => {
    try {
      if (totalCount === 0) {
        setFeedbackMessage({
          type: 'error',
          text: t('employees.noDataToExport'),
        });
        setTimeout(() => setFeedbackMessage(null), 3000);
        return;
      }

      // Backend builds the report (employees + KPI sheet).
      const response = await employeesApi.exportExcel({ lang: language });
      downloadBlobAsFile(response.data, `${t('exportFiles.employees')}.xlsx`);

      setFeedbackMessage({
        type: 'success',
        text: t('employees.exportSuccess', { count: totalCount }),
      });
      setTimeout(() => setFeedbackMessage(null), 3000);
    } catch (error) {
      console.error('Export error:', error);
      setFeedbackMessage({
        type: 'error',
        text: t('employees.exportFailed'),
      });
      setTimeout(() => setFeedbackMessage(null), 3000);
    }
  };

  // Pagination controls
  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const getVisiblePages = () => {
    const pages: number[] = [];
    const maxVisible = 4;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    const end = Math.min(totalPages, start + maxVisible - 1);

    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  };

  // Full-page skeleton only on cold load (no cached data yet). Warm cache
  // and background refetches fall through to the regular render.
  if (loading && employees.length === 0) {
    return (
      <PageContainer>
        <Navbar />
        <GoalsSkeleton />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Navbar />
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        {/* Breadcrumb */}
        <Breadcrumb
          items={organizationId ? [
            { label: t('navigation.home') },
            { label: t('nav.organizations'), route: 'Organizations' },
            { label: organizationName || '', route: 'OrganizationDetail', params: { id: organizationId } },
            { label: t('navigation.humanCapital') },
          ] : [
            { label: t('navigation.home'), route: 'DashboardStack' },
            { label: t('navigation.humanCapital') },
          ]}
        />

        <PageHeader
          title={t('navigation.humanCapital')}
          actions={
            <View style={styles.actionButtons}>
              {!isViewOnly && (
                <>
                  <Button
                    variant="primary"
                    onPress={() => navigation.navigate('AddEmployee', { source: 'employees' })}
                    leadingIcon={<PlusIcon size={16} color={tokens.colors.raw.white} />}
                  >
                    {t('employees.addEmployee')}
                  </Button>
                  <Button
                    variant="secondary"
                    onPress={() => navigation.navigate('ImportEmployees')}
                    trailingIcon={<FileInputIcon size={16} color={themeColors.textPrimary} />}
                  >
                    {t('employees.importList')}
                  </Button>
                </>
              )}
              <Button
                variant="secondary"
                onPress={handleExportExcel}
                leadingIcon={<ExportIcon size={16} color={themeColors.textSecondary} />}
              >
                {t('employees.exportExcel')}
              </Button>
            </View>
          }
          contentTrailing={
          <View style={styles.filterRow}>
          {hasActiveFilters && (
            <TouchableOpacity style={styles.clearFilterButton} onPress={clearFilters}>
              <XIcon size={14} color={tokens.colors.raw.red["500"]} />
              <Text style={styles.clearFilterText}>{t('filters.clearFilter')}</Text>
            </TouchableOpacity>
          )}

          <TouchableOpacity style={[styles.applyFilterBtn, { backgroundColor: themeColors.sectionBg }]} onPress={applyFilters}>
            <Text style={[styles.applyFilterBtnText, { color: themeColors.textPrimary }]}>{t('common.applyFilter')}</Text>
          </TouchableOpacity>

          {/* Status dropdown */}
          <View style={[styles.filterDropdownContainer, { zIndex: 30 }]}>
            <View ref={statusAnchor.ref as any} collapsable={false}>
              <TouchableOpacity
                style={[styles.filterDropdown, { backgroundColor: themeColors.card, borderColor: filterStatus !== 'all' ? COLORS.primary : themeColors.cardBorder }]}
                onPress={() => {
                  if (!showStatusDropdown) statusAnchor.measure();
                  setShowStatusDropdown(!showStatusDropdown);
                  setShowTypeDropdown(false);
                  setShowDepartmentDropdown(false);
                }}
              >
                <ChevronDownIcon size={14} color={filterStatus !== 'all' ? COLORS.primary : themeColors.textSecondary} />
                <Text style={[styles.filterDropdownText, { color: filterStatus !== 'all' ? COLORS.primary : themeColors.textPrimary }]}>
                  {filterStatus === 'all' ? t('filters.allStatuses') : filterStatus === 'active' ? t('employees.active') : t('employees.inactive')}
                </Text>
              </TouchableOpacity>
            </View>
            <DropdownPortal
              open={showStatusDropdown}
              onClose={() => setShowStatusDropdown(false)}
              pos={statusAnchor.pos}
              backgroundColor={themeColors.card}
              borderColor={themeColors.cardBorder}
            >
              {['all', 'active', 'inactive'].map((status, index, arr) => (
                <TouchableOpacity
                  key={status}
                  style={[styles.dropdownItem, { borderBottomColor: themeColors.border }, index === arr.length - 1 && { borderBottomWidth: 0 }]}
                  onPress={() => {
                    setFilterStatus(status);
                    setShowStatusDropdown(false);
                  }}
                >
                  <View style={styles.dropdownItemRow}>
                    {filterStatus === status && <CheckIcon size={16} color={COLORS.primary} />}
                    <Text style={[styles.dropdownItemText, { color: themeColors.textPrimary }, filterStatus === status && styles.dropdownItemTextActive]}>
                      {status === 'all' ? t('filters.allStatuses') : status === 'active' ? t('employees.active') : t('employees.inactive')}
                    </Text>
                  </View>
                </TouchableOpacity>
              ))}
            </DropdownPortal>
          </View>

          {/* Type dropdown */}
          <View style={[styles.filterDropdownContainer, { zIndex: 20 }]}>
            <View ref={typeAnchor.ref as any} collapsable={false}>
              <TouchableOpacity
                style={[styles.filterDropdown, { backgroundColor: themeColors.card, borderColor: filterType !== 'all' ? COLORS.primary : themeColors.cardBorder }]}
                onPress={() => {
                  if (!showTypeDropdown) typeAnchor.measure();
                  setShowTypeDropdown(!showTypeDropdown);
                  setShowStatusDropdown(false);
                  setShowDepartmentDropdown(false);
                }}
              >
                <ChevronDownIcon size={14} color={filterType !== 'all' ? COLORS.primary : themeColors.textSecondary} />
                <Text style={[styles.filterDropdownText, { color: filterType !== 'all' ? COLORS.primary : themeColors.textPrimary }]}>
                  {filterType === 'all' ? t('filters.allTypes') : getTypeLabel(filterType)}
                </Text>
              </TouchableOpacity>
            </View>
            <DropdownPortal
              open={showTypeDropdown}
              onClose={() => setShowTypeDropdown(false)}
              pos={typeAnchor.pos}
              backgroundColor={themeColors.card}
              borderColor={themeColors.cardBorder}
            >
              {['all', 'employee', 'manager'].map((type, index, arr) => (
                <TouchableOpacity
                  key={type}
                  style={[styles.dropdownItem, { borderBottomColor: themeColors.border }, index === arr.length - 1 && { borderBottomWidth: 0 }]}
                  onPress={() => {
                    setFilterType(type);
                    setShowTypeDropdown(false);
                  }}
                >
                  <View style={styles.dropdownItemRow}>
                    {filterType === type && <CheckIcon size={16} color={COLORS.primary} />}
                    <Text style={[styles.dropdownItemText, { color: themeColors.textPrimary }, filterType === type && styles.dropdownItemTextActive]}>
                      {type === 'all' ? t('filters.allTypes') : getTypeLabel(type)}
                    </Text>
                  </View>
                </TouchableOpacity>
              ))}
            </DropdownPortal>
          </View>

          {/* Department dropdown */}
          <View style={[styles.filterDropdownContainer, { zIndex: 10 }]}>
            <View ref={deptAnchor.ref as any} collapsable={false}>
              <TouchableOpacity
                style={[styles.filterDropdown, { backgroundColor: themeColors.card, borderColor: filterDepartment !== 'all' ? COLORS.primary : themeColors.cardBorder }]}
                onPress={() => {
                  if (!showDepartmentDropdown) deptAnchor.measure();
                  setShowDepartmentDropdown(!showDepartmentDropdown);
                  setShowStatusDropdown(false);
                  setShowTypeDropdown(false);
                }}
              >
                <ChevronDownIcon size={14} color={filterDepartment !== 'all' ? COLORS.primary : themeColors.textSecondary} />
                <Text style={[styles.filterDropdownText, { color: filterDepartment !== 'all' ? COLORS.primary : themeColors.textPrimary }]} numberOfLines={1}>
                  {filterDepartment === 'all' ? t('filters.allDepartments') : departments.find(d => d.id.toString() === filterDepartment)?.name || t('filters.allDepartments')}
                </Text>
              </TouchableOpacity>
            </View>
            <DropdownPortal
              open={showDepartmentDropdown}
              onClose={() => setShowDepartmentDropdown(false)}
              pos={deptAnchor.pos}
              backgroundColor={themeColors.card}
              borderColor={themeColors.cardBorder}
              maxHeight={250}
            >
              <ScrollView style={{ maxHeight: 230 }} nestedScrollEnabled showsVerticalScrollIndicator>
                <TouchableOpacity
                  style={[styles.dropdownItem, { borderBottomColor: themeColors.border }]}
                  onPress={() => {
                    setFilterDepartment('all');
                    setShowDepartmentDropdown(false);
                  }}
                >
                  <View style={styles.dropdownItemRow}>
                    {filterDepartment === 'all' && <CheckIcon size={16} color={COLORS.primary} />}
                    <Text style={[styles.dropdownItemText, { color: themeColors.textPrimary }, filterDepartment === 'all' && styles.dropdownItemTextActive]}>
                      {t('filters.allDepartments')}
                    </Text>
                  </View>
                </TouchableOpacity>
                {departments.map((dept, index) => (
                  <TouchableOpacity
                    key={dept.id}
                    style={[styles.dropdownItem, { borderBottomColor: themeColors.border }, index === departments.length - 1 && { borderBottomWidth: 0 }]}
                    onPress={() => {
                      setFilterDepartment(dept.id.toString());
                      setShowDepartmentDropdown(false);
                    }}
                  >
                    <View style={styles.dropdownItemRow}>
                      {filterDepartment === dept.id.toString() && <CheckIcon size={16} color={COLORS.primary} />}
                      <Text style={[styles.dropdownItemText, { color: themeColors.textPrimary }, filterDepartment === dept.id.toString() && styles.dropdownItemTextActive]} numberOfLines={1}>
                        {dept.name}
                      </Text>
                    </View>
                  </TouchableOpacity>
                ))}
              </ScrollView>
            </DropdownPortal>
          </View>
        </View>
          }
          style={{ marginBottom: tokens.spacing.absolute["6"] }}
        />

        {/* Table - No card wrapper, directly on background */}
        <View style={styles.tableContainer}>
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={Platform.OS === 'web'}
            contentContainerStyle={{ flexGrow: 1 }}
          >
            <View style={{ flex: 1, minWidth: '100%' }}>
              {/* Table Header */}
              <View style={[styles.tableHeader, { backgroundColor: themeColors.tableHeader }]}>
                {/* Actions column - no header text */}
                <View style={[styles.tableCell, styles.actionsCell]} />
                <View style={[styles.tableCell, styles.statusCell]}>
                  <Text style={[styles.tableHeaderText, { color: themeColors.textPrimary }]}>{t('employees.status')}</Text>
                </View>
                <View style={[styles.tableCell, styles.performanceCell]}>
                  <Text style={[styles.tableHeaderText, { color: themeColors.textPrimary }]}>{t('employees.performance')}</Text>
                </View>
                <View style={[styles.tableCell, styles.typeCell]}>
                  <Text style={[styles.tableHeaderText, { color: themeColors.textPrimary }]}>{t('employees.type')}</Text>
                </View>
                <View style={[styles.tableCell, styles.phoneCell]}>
                  <Text style={[styles.tableHeaderText, { color: themeColors.textPrimary }]}>{t('employees.phone')}</Text>
                </View>
                <View style={[styles.tableCell, styles.emailCell]}>
                  <Text style={[styles.tableHeaderText, { color: themeColors.textPrimary }]}>{t('employees.email')}</Text>
                </View>
                <View style={[styles.tableCell, styles.departmentCell]}>
                  <Text style={[styles.tableHeaderText, { color: themeColors.textPrimary }]}>{t('employees.department')}</Text>
                </View>
                <View style={[styles.tableCell, styles.nameCell]}>
                  <Text style={[styles.tableHeaderText, { color: themeColors.textPrimary }]}>{t('employees.userName')}</Text>
                </View>
                <View style={[styles.tableCell, styles.accountCell]}>
                  <Text style={[styles.tableHeaderText, { color: themeColors.textPrimary }]}>{t('employees.accountName')}</Text>
                </View>
              </View>

              {/* Table Rows */}
              {employees.length > 0 ? (
                employees.map((employee, index) => (
                  <View
                    key={employee.id}
                    style={[
                      styles.tableRow,
                      { backgroundColor: index % 2 === 0 ? themeColors.tableRow : themeColors.tableRowAlt },
                    ]}
                  >
                    {/* Actions */}
                    <View style={[styles.tableCell, styles.actionsCell]}>
                      <View style={styles.actionIcons}>
                        {!isViewOnly && (
                          <>
                            <IconButton
                              variant="ghost"
                              size="sm"
                              icon={<TrashIcon color={tokens.colors.raw.red["500"]} />}
                              accessibilityLabel={t('common.delete')}
                              onPress={() => handleDelete(employee)}
                            />
                            <IconButton
                              variant="ghost"
                              size="sm"
                              icon={<EditIcon color={themeColors.textSecondary} />}
                              accessibilityLabel={t('common.edit')}
                              onPress={() => navigation.navigate('AddEmployee', { employeeId: employee.id, source: 'employees' })}
                            />
                          </>
                        )}
                        <IconButton
                          variant="ghost"
                          size="sm"
                          icon={<EyeIcon color={themeColors.textSecondary} />}
                          accessibilityLabel={t('common.view')}
                          onPress={() => navigation.navigate('EmployeeDetail', { id: employee.id, source: 'employees' })}
                        />
                      </View>
                    </View>

                    {/* Status */}
                    <View style={[styles.tableCell, styles.statusCell]}>
                      <View style={[styles.statusBadge, { backgroundColor: getStatusColor(employee.is_active ?? false) }]}>
                        <Text style={styles.statusBadgeText}>{getStatusLabel(employee.is_active ?? false)}</Text>
                      </View>
                    </View>

                    {/* Performance */}
                    <View style={[styles.tableCell, styles.performanceCell]}>
                      <View style={styles.performanceContainer}>
                        <Text style={[styles.performanceText, { color: themeColors.textPrimary }]}>
                          {employee.overall_achievement || 0} %
                        </Text>
                        <View style={[styles.performanceCircle, { borderColor: tokens.colors.raw.green["500"] }]}>
                          <View style={[styles.performanceInner, { backgroundColor: tokens.colors.raw.green["500"] }]} />
                        </View>
                      </View>
                    </View>

                    {/* Type */}
                    <View style={[styles.tableCell, styles.typeCell]}>
                      <View style={styles.typeBadge}>
                        <Text style={styles.typeBadgeText}>{t(`employees.userTypes.${employee.user_type || 'employee'}`)}</Text>
                      </View>
                    </View>

                    {/* Phone */}
                    <View style={[styles.tableCell, styles.phoneCell]}>
                      <Text style={[styles.cellText, { color: themeColors.textPrimary }]}>{employee.phone || '-'}</Text>
                    </View>

                    {/* Email */}
                    <View style={[styles.tableCell, styles.emailCell]}>
                      <Text style={[styles.cellText, { color: themeColors.textPrimary }]} numberOfLines={1}>
                        {employee.email || '-'}
                      </Text>
                    </View>

                    {/* Department */}
                    <View style={[styles.tableCell, styles.departmentCell]}>
                      <Text style={[styles.cellText, { color: themeColors.textPrimary }]} numberOfLines={1}>
                        {employee.organizational_unit_name_ar || employee.organizational_unit_name || '-'}
                      </Text>
                    </View>

                    {/* Name */}
                    <View style={[styles.tableCell, styles.nameCell]}>
                      <Text style={[styles.cellText, { color: themeColors.textPrimary }]}>{employee.name_ar}</Text>
                    </View>

                    {/* Account */}
                    <View style={[styles.tableCell, styles.accountCell]}>
                      <Text style={[styles.cellText, { color: themeColors.textPrimary }]}>
                        {employee.employee_number || `emp${employee.id}`}
                      </Text>
                    </View>
                  </View>
                ))
              ) : (
                <View style={styles.emptyRow}>
                  <Text style={[styles.emptyText, { color: themeColors.textSecondary }]}>{t('employees.noEmployees')}</Text>
                </View>
              )}
            </View>
          </ScrollView>
        </View>

        {/* Pagination */}
        <View style={[styles.paginationContainer, { flexDirection: rtlRow }]}>
          <Text style={[styles.paginationInfo, { color: themeColors.textSecondary }]}>
            {t('pagination.showing', { start: Math.min((currentPage - 1) * pageSize + 1, totalCount), end: Math.min(currentPage * pageSize, totalCount), total: totalCount })}
          </Text>

          <View style={[styles.paginationControls, { flexDirection: rtlRow }]}>
            <TouchableOpacity
              style={[styles.paginationButton, currentPage === 1 && styles.paginationButtonDisabled]}
              onPress={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
            >
              <Text style={[styles.paginationButtonText, { color: currentPage === 1 ? tokens.colors.raw.gray["400"] : themeColors.textPrimary }]}>{t('common.previous')}</Text>
              <ChevronRightIcon size={16} color={currentPage === 1 ? tokens.colors.raw.gray["400"] : themeColors.textPrimary} />
            </TouchableOpacity>

            <View style={[styles.pageNumbers, { flexDirection: rtlRow }]}>
              {getVisiblePages().map((page) => (
                <TouchableOpacity
                  key={page}
                  style={[styles.pageNumber, currentPage === page && styles.pageNumberActive]}
                  onPress={() => goToPage(page)}
                >
                  <Text style={[styles.pageNumberText, currentPage === page && styles.pageNumberTextActive]}>
                    {page}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>

            <TouchableOpacity
              style={[styles.paginationButton, currentPage === totalPages && styles.paginationButtonDisabled]}
              onPress={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              <ChevronLeftIcon size={16} color={currentPage === totalPages ? tokens.colors.raw.gray["400"] : themeColors.textPrimary} />
              <Text style={[styles.paginationButtonText, { color: currentPage === totalPages ? tokens.colors.raw.gray["400"] : themeColors.textPrimary }]}>{t('common.next')}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </ScrollView>

      {/* Feedback Message */}
      {feedbackMessage && (
        <View style={[
          styles.feedbackMessage,
          feedbackMessage.type === 'success' && styles.feedbackSuccess,
          feedbackMessage.type === 'info' && styles.feedbackInfo,
          feedbackMessage.type === 'error' && styles.feedbackError,
        ]}>
          <Text style={styles.feedbackText}>{feedbackMessage.text}</Text>
        </View>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        visible={deleteModalVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setDeleteModalVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>{t('common.confirmDelete')}</Text>
            <Text style={styles.modalMessage}>
              {t('employees.deleteConfirm', { name: employeeToDelete?.name_ar })}
            </Text>
            <View style={styles.modalButtons}>
              <TouchableOpacity
                style={styles.modalCancelButton}
                onPress={() => setDeleteModalVisible(false)}
              >
                <Text style={styles.modalCancelButtonText}>{t('common.cancel')}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.modalDeleteButton, deleting && styles.modalButtonDisabled]}
                onPress={confirmDelete}
                disabled={deleting}
              >
                {deleting ? (
                  <ActivityIndicator size="small" color={tokens.colors.raw.white} />
                ) : (
                  <Text style={styles.modalDeleteButtonText}>{t('common.delete')}</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </PageContainer>
  );
}

const getStyles = (isRTL: boolean) => StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: tokens.spacing.absolute["6"],
    paddingBottom: tokens.spacing.absolute["10"],
  },
  tableContainer: {
    width: '100%',
    marginBottom: tokens.spacing.absolute["5"],
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: tokens.spacing.absolute["3"],
  },
  loadingText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
  },
  actionButtons: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["3"],
  },
  filterRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["4"],
    marginTop: tokens.spacing.absolute["4"],
    position: 'relative',
    zIndex: 1000,
  },
  applyFilterBtn: {
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["2"],
    borderRadius: tokens.radii.absolute["radius-8"],
    backgroundColor: tokens.colors.raw.slate["50"],
  },
  applyFilterBtnText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
  },
  clearFilterButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["2"],
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: '#FCA5A5',
    backgroundColor: tokens.colors.raw.red["50"],
    gap: tokens.spacing.absolute["1_5"],
  },
  clearFilterText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.red["500"],
  },
  filterDropdownContainer: {
    flex: 1,
    maxWidth: 200,
    position: 'relative',
  },
  filterDropdown: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["1_5"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["2"],
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
  },
  filterDropdownText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    textAlign: getTextAlign(isRTL),
  },
  dropdownMenu: {
    position: 'absolute',
    top: 44,
    start: 0,
    end: 0,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    shadowColor: tokens.colors.raw.black,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 10,
    zIndex: 9999,
    overflow: 'hidden',
  },
  dropdownItem: {
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["3"],
    borderBottomWidth: tokens.spacing.absolute["px"],
  },
  dropdownItemRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["2"],
  },
  dropdownItemText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    textAlign: getTextAlign(isRTL),
  },
  dropdownItemTextActive: {
    color: COLORS.primary,
    fontFamily: FONTS.medium,
  },
  tableHeader: {
    flexDirection: 'row',
    paddingVertical: tokens.spacing.absolute["3"],
    paddingHorizontal: tokens.spacing.absolute["4"],
    borderBottomWidth: tokens.spacing.absolute["px"],
    borderBottomColor: tokens.colors.raw.slate["200"],
    flex: 1,
  },
  tableHeaderText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    textAlign: getTextAlign(isRTL),
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: tokens.spacing.absolute["3"],
    paddingHorizontal: tokens.spacing.absolute["4"],
    borderBottomWidth: tokens.spacing.absolute["px"],
    borderBottomColor: tokens.colors.raw.slate["100"],
    flex: 1,
  },
  tableCell: {
    justifyContent: 'center',
    alignItems: 'flex-end',
    paddingHorizontal: tokens.spacing.absolute["2"],
  },
  actionsCell: {
    width: 100,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: tokens.spacing.absolute["2"],
  },
  statusCell: {
    width: 80,
    alignItems: 'center',
  },
  performanceCell: {
    width: 90,
  },
  typeCell: {
    width: 120,
    minWidth: 120,
  },
  phoneCell: {
    width: 130,
  },
  emailCell: {
    flex: 1,
    minWidth: 200,
  },
  departmentCell: {
    flex: 1,
    minWidth: 180,
  },
  nameCell: {
    flex: 1,
    minWidth: 150,
  },
  accountCell: {
    width: 120,
  },
  cellText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    textAlign: getTextAlign(isRTL),
  },
  actionIcons: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["1"],
  },
  statusBadge: {
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["1"],
    borderRadius: tokens.radii.absolute["radius-4"],
  },
  statusBadgeText: {
    color: tokens.colors.raw.white,
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.medium,
  },
  performanceContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
  },
  performanceText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
  },
  performanceCircle: {
    width: 24,
    height: 24,
    borderRadius: tokens.radii.absolute["radius-12"],
    borderWidth: 3,
    justifyContent: 'center',
    alignItems: 'center',
  },
  performanceInner: {
    width: 10,
    height: 10,
    borderRadius: tokens.radii.absolute["radius-5"],
  },
  typeBadge: {
    backgroundColor: tokens.colors.raw.blue["500"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["1"],
    borderRadius: tokens.radii.absolute["radius-4"],
  },
  typeBadgeText: {
    color: tokens.colors.raw.white,
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.medium,
  },
  emptyRow: {
    padding: tokens.spacing.absolute["10"],
    alignItems: 'center',
  },
  emptyText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
  },
  paginationContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: tokens.spacing.absolute["4"],
  },
  paginationControls: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
  },
  paginationButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["2"],
    gap: tokens.spacing.absolute["1"],
  },
  paginationButtonDisabled: {
    opacity: 0.5,
  },
  paginationButtonText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
  },
  pageNumbers: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["1"],
  },
  pageNumber: {
    width: 32,
    height: 32,
    borderRadius: tokens.radii.absolute["radius-8"],
    justifyContent: 'center',
    alignItems: 'center',
  },
  pageNumberActive: {
    backgroundColor: tokens.colors.raw.slate["100"],
  },
  pageNumberText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["500"],
  },
  pageNumberTextActive: {
    color: '#0F172A',
    fontFamily: FONTS.medium,
  },
  paginationInfo: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
  },
  // Modal styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-12"],
    padding: tokens.spacing.absolute["6"],
    width: '90%',
    maxWidth: 400,
    shadowColor: tokens.colors.raw.black,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 10,
  },
  modalTitle: {
    fontSize: tokens.typography.paragraph["paragraph subheading font size"],
    fontFamily: FONTS.bold,
    color: '#0F172A',
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["3"],
  },
  modalMessage: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.slate["500"],
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["6"],
    lineHeight: 22,
  },
  modalButtons: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    gap: tokens.spacing.absolute["3"],
  },
  modalCancelButton: {
    flex: 1,
    paddingVertical: tokens.spacing.absolute["3"],
    paddingHorizontal: tokens.spacing.absolute["4"],
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    backgroundColor: tokens.colors.raw.white,
    alignItems: 'center',
  },
  modalCancelButtonText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.raw.slate["500"],
  },
  modalDeleteButton: {
    flex: 1,
    paddingVertical: tokens.spacing.absolute["3"],
    paddingHorizontal: tokens.spacing.absolute["4"],
    borderRadius: tokens.radii.absolute["radius-8"],
    backgroundColor: tokens.colors.raw.red["500"],
    alignItems: 'center',
  },
  modalButtonDisabled: {
    opacity: 0.6,
  },
  modalDeleteButtonText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.raw.white,
  },
  // Feedback message styles
  feedbackMessage: {
    position: 'absolute',
    bottom: 24,
    start: 24,
    end: 24,
    paddingVertical: tokens.spacing.absolute["3"],
    paddingHorizontal: tokens.spacing.absolute["4"],
    borderRadius: tokens.radii.absolute["radius-8"],
    alignItems: 'center',
    zIndex: 1000,
  },
  feedbackSuccess: {
    backgroundColor: tokens.colors.raw.green["500"],
  },
  feedbackInfo: {
    backgroundColor: tokens.colors.raw.blue["500"],
  },
  feedbackError: {
    backgroundColor: tokens.colors.raw.red["500"],
  },
  feedbackText: {
    color: tokens.colors.raw.white,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    textAlign: 'center',
  },
});

const styles = getStyles(true);
